import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Star } from "lucide-react";

const values = [
  { title: "Precision", desc: "Every order is reviewed by a quality control specialist before delivery. If it's not right, it doesn't ship." },
  { title: "Speed", desc: "7-10 day standard turnaround with rush options down to same-day. We understand that in business, timing is everything." },
  { title: "Transparency", desc: "No hidden setup fees, no surprise charges. The price we quote is the price you pay." },
  { title: "Accountability", desc: "We're a local Dallas business. You can call us directly. If something's wrong, we fix it — period." },
];

const teamMembers = [
  { name: "Tyler M.", role: "Production Manager", years: 12, note: "Oversees all press operations and quality control" },
  { name: "Ana R.", role: "Art Director", years: 8, note: "Print-optimized design and Pantone color specialist" },
  { name: "Derek S.", role: "Account Manager", years: 6, note: "Your point of contact for all DFW orders" },
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Ninja Screen Print | Dallas TX Screen Printing Company | Our Story</title>
        <meta name="description" content="About Ninja Screen Print — Dallas TX's premier custom apparel printing company. Our story, values, team, and commitment to quality screen printing and embroidery in DFW." />
      </Helmet>

      {/* Hero */}
      <div
        className="pt-16 relative min-h-[50vh] flex items-end pb-16 overflow-hidden"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1400&q=80')", backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="relative z-10 ninja-container">
          <div className="red-line" />
          <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">
            About Ninja Screen Print
          </h1>
          <p className="text-neutral-300 font-inter mt-3 text-lg max-w-2xl">
            Dallas's premier custom apparel printing company — built on precision, speed, and the belief that every business deserves print work that commands respect.
          </p>
        </div>
      </div>

      {/* Story */}
      <section className="ninja-section" data-testid="about-story">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="red-line" />
              <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight mb-6">Our Story</h2>
              <div className="space-y-4 text-neutral-300 font-inter text-sm leading-relaxed">
                <p>Ninja Screen Print was built from a simple observation: Dallas businesses deserved a local screen printing partner that combined the quality and speed of the best national print shops with the personal service and accountability of a local business.</p>
                <p>Too many DFW organizations were forced to choose between mediocre local printers and distant online print farms that treated their orders like conveyor belt numbers. We changed that equation.</p>
                <p>We built our operation around three commitments that still drive everything we do: precision in every print, speed in every order, and personal accountability to every client. The name "Ninja" reflects our approach to production — silent, precise, efficient, and deadly effective.</p>
                <p>Today, Ninja Screen Print serves hundreds of Dallas businesses, schools, sports organizations, restaurants, and events annually — from 6-shirt DTF orders for startups to 3,000-shirt bulk runs for major corporate clients. Every order, regardless of size, gets the same Ninja standard.</p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1581092160607-ee22731c9e11?w=700&q=80"
                alt="Screen printing production at Ninja Screen Print Dallas"
                className="w-full object-cover border border-neutral-800"
              />
              <div className="absolute -bottom-4 -left-4 bg-red-600 p-6">
                <div className="font-teko text-4xl text-white leading-none">500+</div>
                <div className="text-red-200 text-xs uppercase tracking-widest font-inter">5-Star Reviews</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="ninja-section bg-[#0d0d0d]" data-testid="about-values">
        <div className="ninja-container">
          <div className="text-center mb-12">
            <div className="w-8 h-px bg-red-600 mx-auto mb-4" />
            <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">What We Stand For</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v) => (
              <div key={v.title} className="ninja-card p-6">
                <div className="w-1 h-6 bg-red-600 mb-4" />
                <h3 className="font-teko text-2xl text-white uppercase tracking-wide mb-3">{v.title}</h3>
                <p className="text-neutral-400 font-inter text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Local Matters */}
      <section className="ninja-section" data-testid="about-local">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="red-line" />
              <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight mb-6">Why Local Matters</h2>
              <div className="space-y-4 text-neutral-300 font-inter text-sm leading-relaxed">
                <p>When you order from a national online print shop, your order is one of thousands on a conveyor belt. You get a tracking number and a prayer that it arrives on time.</p>
                <p>When you order from Ninja Screen Print, you have a local phone number, a local team, and local accountability. If something isn't right — and in custom printing, sometimes things need adjustment — you can call us and we'll make it right immediately.</p>
                <p>We also offer free local delivery throughout DFW on qualifying orders. No UPS delays, no shipping damage, no parking lot meetups. We bring your order to your door.</p>
                <p>And because we live and work in Dallas too, we understand your business, your events, and your community. We're not just vendors — we're your local print partners.</p>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-8">
                {[
                  { v: "2hr", label: "Quote Response" },
                  { v: "7d", label: "Standard Turnaround" },
                  { v: "16+", label: "DFW Cities Served" },
                  { v: "100%", label: "Satisfaction Rate" },
                ].map(({ v, label }) => (
                  <div key={label} className="text-center ninja-card p-4">
                    <div className="font-teko text-4xl text-red-500">{v}</div>
                    <div className="text-xs text-neutral-500 uppercase tracking-widest font-inter">{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1765611800420-92b96b438fa3?w=700&q=80"
                alt="Dallas Texas skyline"
                className="w-full object-cover border border-neutral-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Snippet */}
      <section className="ninja-section bg-[#0d0d0d]" data-testid="about-reviews">
        <div className="ninja-container">
          <div className="text-center mb-12">
            <div className="w-8 h-px bg-red-600 mx-auto mb-4" />
            <h2 className="font-teko text-4xl text-white uppercase tracking-tight">What Dallas Says</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { stars: 5, text: "The most professional screen printing experience in Dallas. On time, exact colors, and delivered right to our office. We use Ninja for everything now.", name: "Rachel B.", co: "Uptown Marketing Firm" },
              { stars: 5, text: "Rush order of 200 shirts in 3 days for our company party. Every shirt was perfect. I've never seen a printer execute that fast at that quality.", name: "James T.", co: "North Dallas Tech" },
              { stars: 5, text: "Our school has used dozens of print shops over the years. Ninja Print is by far the most reliable, most consistent, and most professional we've worked with.", name: "Principal Anderson", co: "Dallas ISD School" },
            ].map((t, i) => (
              <div key={i} className="ninja-card p-6">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.stars)].map((_, s) => <Star key={s} size={12} className="text-red-500 fill-red-500" />)}
                </div>
                <p className="text-neutral-300 font-inter text-sm italic leading-relaxed mb-4">"{t.text}"</p>
                <div className="text-white font-inter text-sm font-medium">{t.name}</div>
                <div className="text-neutral-500 font-inter text-xs">{t.co}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="ninja-container text-center">
          <h2 className="font-teko text-5xl text-white uppercase tracking-tight">Ready to Experience the Ninja Difference?</h2>
          <p className="text-neutral-400 font-inter mt-3 max-w-xl mx-auto">Free quote in 2 hours. Free DFW delivery. Zero compromises.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/contact" data-testid="about-cta-btn" className="ninja-btn-primary font-teko tracking-widest text-xl px-10 py-4">GET A FREE QUOTE</Link>
            <Link to="/services" className="ninja-btn-secondary font-teko tracking-widest text-xl px-10 py-4">VIEW OUR SERVICES</Link>
          </div>
        </div>
      </section>
    </>
  );
}
